import type { ReactElement } from 'react';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { Link } from './Link';
import { letterDateLine, ordinalLine, readsLine } from '../letter-format';
import './LetterCover.css';

export interface LetterCoverProps {
  /** Where the cover opens: the letter's page. */
  to: string;
  writtenAt: string | null | undefined;
  /** The first line of the letter, as prose; the cover clips it to two lines. */
  firstLine: string;
  /** Which letter and what days it reads, when the surface knows (the notebook does; the thread does not). */
  ordinal?: number | null;
  readsFrom?: string | null;
  /** Opened in the notebook already. Unread covers carry the dot. */
  read?: boolean;
  /** `data-log` name of the tap; the letter id rides along. */
  log: string;
  letterId?: string | null;
  className?: string;
}

/**
 * The cover of one of Ember's weekly letters (§4.20, 2026-09-23 二次拍板):
 * the same object wherever a letter is announced — a card on the notebook's
 * Letters tab, a turn of the conversation — and opening it is opening the
 * letter's page, the way an Ember card opens the Ember. The letterhead band
 * (the flame, "A letter from Ember", the day), one or two facts when the
 * surface knows them, the first line in two lines, one door. The words wait
 * on the page; the cover only says that a letter is here and what it opens on.
 */
export function LetterCover({ to, writtenAt, firstLine, ordinal = null, readsFrom = null, read = true, log, letterId = null, className = '' }: LetterCoverProps): ReactElement {
  const reads = readsLine(readsFrom, writtenAt);
  const facts = ordinal ? `${ordinalLine(ordinal)}${reads ? ` · reads ${reads}` : ''}` : '';
  return (
    <Link
      to={to}
      className={`letter-cover${read ? ' is-read' : ' is-unread'}${className ? ` ${className}` : ''}`}
      data-log={log}
      data-log-letter-id={letterId ?? undefined}
      data-log-state={read ? 'read' : 'unread'}
      aria-label={`A letter from Ember, ${letterDateLine(writtenAt)}${read ? '' : ', unread'}`}
    >
      <span className="letter-cover-band">
        <span className="letter-cover-from">
          <span className="letter-cover-mark" aria-hidden><EmberFlame className="letter-cover-flame" /></span>
          A letter from Ember
        </span>
        <span className="letter-cover-date">{letterDateLine(writtenAt, 'short')}</span>
        {!read && <span className="letter-cover-dot" aria-hidden />}
      </span>
      <span className="letter-cover-body">
        {facts && <span className="letter-cover-facts">{facts}</span>}
        <span className="letter-cover-line" dir="auto">{firstLine}</span>
      </span>
      <span className="letter-cover-open">
        <span>{read ? 'Read again' : 'Open the letter'}</span>
        <svg viewBox="0 0 24 24" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
      </span>
    </Link>
  );
}
